import type { ResultRow } from './InviteResultsPanel'

import { BillingProvider, CostCenterResponse } from '@/api/generated'
import { ResendInvitationResult } from '@/api/members'
import type { MemberOption } from '@/components/MemberMultiSelect'
import { formatLastUsedAt } from '@/pages/app/UsageV2/TeamMembersTab/relativeDate'
import { Invite, InviteStatus, Member } from '@/types'
import { formatExpirationDate } from '@/utils/dates'

export interface ShouldRenderRoleDropdownArgs {
  adminView: boolean
  isSelf: boolean
  memberRole?: string | null
}

/**
 * Admins can edit every non-superadmin row, plus their own row.
 * This preserves the "don't edit other superadmins" rule while
 * allowing creator/self role changes.
 */
export const shouldRenderRoleDropdown = ({ adminView, isSelf, memberRole }: ShouldRenderRoleDropdownArgs): boolean => {
  if (!adminView || !memberRole) return false
  return isSelf || memberRole.toLowerCase() !== 'superadmin'
}

const RESENDABLE_INVITE_STATUSES: ReadonlySet<InviteStatus> = new Set<InviteStatus>(['pending', 'error'])

export const isResendableInvite = (invite: Pick<Invite, 'status'>): boolean =>
  RESENDABLE_INVITE_STATUSES.has(invite.status)

/** Invites sent before the server recorded send times have no `lastSentAt`, so they have no label. */
const lastSentLabel = (invite: Pick<Invite, 'lastSentAt'>): string | null =>
  invite.lastSentAt
    ? `Last sent ${formatLastUsedAt(Date.parse(invite.lastSentAt) / 1000, { sentenceCase: true })}`
    : null

export const inviteSubtitle = (invite: Pick<Invite, 'status' | 'expiresAt' | 'lastSentAt'>): string =>
  (isResendableInvite(invite) && lastSentLabel(invite)) || formatExpirationDate(invite.expiresAt)

/** `skipped_cooldown` means the server emailed it within the last minute, which is what the admin asked for. */
export const wasResent = (result: ResendInvitationResult): boolean =>
  result.outcome === 'sent' || result.outcome === 'skipped_cooldown'

/** Ids "Resend all" sends, and so its count. */
export const resendableInviteIds = (invites: Invite[]): string[] =>
  invites.filter(isResendableInvite).map((invite) => invite.id)

/** Picker rows for "Resend pending invites", in table order, labelled with failure and last send. */
export const resendInviteOptions = (invites: Invite[]): MemberOption[] =>
  invites.filter(isResendableInvite).map((invite) => ({
    userId: invite.id,
    email: invite.email,
    firstName: '',
    lastName: '',
    tag: [invite.status === 'error' ? 'Failed' : null, lastSentLabel(invite)].filter(Boolean).join(' · ') || undefined,
  }))

export const resendResultRows = (results: ResendInvitationResult[], invites: Invite[]): ResultRow[] => {
  const emailById = new Map(invites.map((invite) => [invite.id, invite.email]))
  const resentCount = results.filter(wasResent).length
  const notResent = results
    .filter((result) => !wasResent(result))
    .map((result) => emailById.get(result.invitation_id) ?? result.invitation_id)

  const rows: ResultRow[] = []
  if (resentCount > 0) rows.push({ label: `Resent (${resentCount})`, kind: 'success' })
  if (notResent.length > 0) {
    rows.push({
      label: `Couldn't resend (${notResent.length})`,
      detail: `${notResent.join(', ')}. If this keeps happening, revoke the invite and add them again.`,
      kind: 'error',
    })
  }
  return rows
}

const SEAT_CHANGE_PROVIDERS = new Set<string>([BillingProvider.SelfServe, BillingProvider.SalesOwned])

/** The server still has the final say on each change. */
export const editableSeatCostCenterIds = (
  costCenters: Pick<CostCenterResponse, 'id' | 'billing_provider'>[] | undefined
) =>
  new Set(
    (costCenters ?? [])
      .filter((costCenter) => SEAT_CHANGE_PROVIDERS.has(costCenter.billing_provider))
      .map((costCenter) => costCenter.id)
  )

/** Mirrors the server's per-member rules: IT admins hold no product seat and members without a team can't change. */
export const isSeatEditable = (member: Member, editableCostCenterIds: Set<string>) =>
  member.role?.toLowerCase() !== 'it_admin' && !!member.costCenterId && editableCostCenterIds.has(member.costCenterId)
